import React, { useState, useCallback, useMemo, useRef } from 'react';
import { useClickAway } from 'ahooks';
import { Popover, List } from 'tea-component';
import classNames from 'classnames';
import { useIntl } from 'umi';
import CommonBubble from '@home/components/CommonBubble';
import SvgIcon from '@home/components/SvgIcon';
import {
  AddRowColActionType,
  IAddRowColOptionItem,
  ADD_ROW_COL_OPTIONS,
  RowColType,
  getAddRowColLabelKey,
} from '../../constants';
import SmartFillForm from '../SmartFillForm';
import tableAddIcon from '@/pages/aigc/assets/svg/agent-table-add.svg';
import './index.scss';

/**
 * 操作处理器映射类型
 * 用于自定义每个操作的处理逻辑
 */
export type TActionHandlers = Partial<Record<AddRowColActionType, () => void>>;

interface IAddRowColButtonProps {
  /** 类型：行或列 */
  type: RowColType;
  /** 是否英文环境 */
  isEn?: boolean;
  /** Popover 位置 */
  placement?: 'top' | 'bottom' | 'left' | 'right';
  /** 新增空白回调 */
  onAddBlank?: () => void;
  /** 智能填充确认回调 */
  onSmartFillConfirm?: (values: any) => void;
  /** 自定义操作处理器（优先级高于默认处理） */
  customHandlers?: TActionHandlers;
  /** 自定义菜单选项（覆盖默认选项） */
  customOptions?: IAddRowColOptionItem[];
  /** 自定义类名 */
  className?: string;
  /** 是否禁用新增空白操作（达到最大行数时禁用） */
  disableAddBlank?: boolean;
}

/**
 * 渲染菜单项图标
 */
const renderMenuIcon = (option: IAddRowColOptionItem): React.ReactNode => {
  const iconClassName = classNames(
    'dba-add-row-col-menu__icon',
    option.iconClassName
  );

  if (option.iconType === 'icon') {
    return (
      <span className={iconClassName}>
        {option.icon as React.ReactNode}
      </span>
    );
  }

  return (
    <span className={iconClassName}>
      <img
        src={option.icon as string}
        alt=""
      />
    </span>
  );
};

/**
 * 添加行/列按钮组件
 * 使用 Popover 实现下拉菜单，支持新增空白和智能填充两种操作
 * 支持自定义操作处理器和菜单选项
 */
export const AddRowColButton: React.FC<IAddRowColButtonProps> = ({
  type,
  isEn = false,
  placement = 'bottom',
  onAddBlank,
  onSmartFillConfirm,
  customHandlers,
  customOptions,
  className,
  disableAddBlank = false,
}) => {
  const { formatMessage } = useIntl();
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [smartFillVisible, setSmartFillVisible] = useState(false);
  const smartFillPopoverRef = useRef<HTMLDivElement>(null);

  /**
   * 点击外部关闭智能填充弹层
   */
  useClickAway(
    () => {
      if (smartFillVisible) {
        setSmartFillVisible(false);
      }
    },
    smartFillPopoverRef,
    'mousedown'
  );

  const isRow = type === RowColType.ROW;
  const typeLabel = formatMessage({ id: isRow ? 'dba_smart_table_row' : 'dba_smart_table_column' });

  // 使用自定义选项或默认选项
  const menuOptions = customOptions || ADD_ROW_COL_OPTIONS;

  /**
   * 默认操作处理器映射
   * 使用 Map 替代 switch 语句，保持简洁
   */
  const defaultHandlers = useMemo<Record<AddRowColActionType, () => void>>(() => ({
    [AddRowColActionType.ADD_BLANK]: () => {
      onAddBlank?.();
      setPopoverVisible(false);
    },
    [AddRowColActionType.SMART_FILL]: () => {
      // 关闭当前菜单，打开智能填充 Popover
      setPopoverVisible(false);
      setSmartFillVisible(true);
    },
  }), [typeLabel, onAddBlank, formatMessage]);

  /**
   * 处理菜单项点击
   * 优先使用自定义处理器，否则使用默认处理器
   */
  const handleActionClick = useCallback((actionType: AddRowColActionType) => {
    // 优先使用自定义处理器
    const handler = customHandlers?.[actionType] || defaultHandlers[actionType];
    
    if (handler) {
      handler();
      // 如果是自定义处理器，自动关闭菜单
      if (customHandlers?.[actionType]) {
        setPopoverVisible(false);
      }
    } else {
      console.warn(`未处理的操作类型: ${actionType}`);
      setPopoverVisible(false);
    }
  }, [customHandlers, defaultHandlers]);

  const handleSmartFillConfirm = useCallback((values: any) => {
    onSmartFillConfirm?.(values);
    setSmartFillVisible(false);
  }, [onSmartFillConfirm]);

  const handleSmartFillCancel = useCallback(() => {
    setSmartFillVisible(false);
  }, []);

  // 下拉菜单内容 - 使用 List 组件与导出下拉列表一致
  const menuContent = (
    <List type="option" className="dba-add-row-col-menu">
      {menuOptions.map((option) => {
        // 判断当前选项是否禁用
        const isDisabled = option.key === AddRowColActionType.ADD_BLANK && disableAddBlank;
        
        return (
          <List.Item
            key={option.key}
            onClick={() => !isDisabled && handleActionClick(option.key)}
            disabled={isDisabled}
          >
            <span className={classNames('dba-add-row-col-menu__item', { 'dba-add-row-col-menu__item--disabled': isDisabled })}>
              {renderMenuIcon(option)}
              <span className="dba-add-row-col-menu__label">
                {formatMessage({ id: getAddRowColLabelKey(option) })}{typeLabel}
              </span>
            </span>
          </List.Item>
        );
      })}
    </List>
  );

  // 智能填充表单 Popover 内容
  const smartFillContent = (
    <div className="dba-smart-fill-popover" ref={smartFillPopoverRef}>
      <div className="dba-smart-fill-popover__header">
        <span className="dba-smart-fill-popover__title">
          {formatMessage({ id: 'dba_smart_table_smart_fill' })}
        </span>
        <SvgIcon
          type="clonse"
          className="dba-smart-fill-popover__close"
          onClick={handleSmartFillCancel}
        />
      </div>
      <SmartFillForm
        type={type}
        isEn={isEn}
        onConfirm={handleSmartFillConfirm}
        onCancel={handleSmartFillCancel}
      />
    </div>
  );

  const tooltip = isRow 
    ? formatMessage({ id: 'dba_smart_table_add_on_bottom' })
    : formatMessage({ id: 'dba_smart_table_add_on_right' });

  return (
    <>
      {/* 菜单 Popover */}
      <Popover
        placement={placement}
        visible={popoverVisible}
        onVisibleChange={setPopoverVisible}
        trigger="click"
        overlay={menuContent}
      >
        {/* 智能填充 Popover - 点击外部不关闭，行操作使用 top 方向避免被底部遮挡 */}
        <Popover
          placement={isRow ? 'top' : placement}
          visible={smartFillVisible}
          onVisibleChange={(visible) => {
            // 只允许通过关闭按钮关闭，点击外部不关闭
            if (visible) {
              setSmartFillVisible(true);
            }
          }}
          trigger="click"
          overlay={smartFillContent}
        >
          <CommonBubble
            content={tooltip}
            placement={isRow ? 'top' : 'left'}
            overlayClassName="dba-add-row-col-button__bubble"
          >
            <button
              className={classNames('dba-add-row-col-button', className)}
              onClick={() => {
                if (!smartFillVisible) {
                  setPopoverVisible(!popoverVisible);
                }
              }}
              aria-label={tooltip}
              tabIndex={0}
            >
              <img
                src={tableAddIcon}
                alt={`添加${typeLabel}`}
                className="dba-add-row-col-button__icon"
              />
            </button>
          </CommonBubble>
        </Popover>
      </Popover>
    </>
  );
};

export default AddRowColButton;
